import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Box, CircularProgress, Alert, MenuItem, Typography, IconButton,
  Avatar, Divider, InputAdornment, Stack, Chip, useTheme
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Person as PersonIcon, 
  CardMembership as CardIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import { getUsers, createCardForUser } from '../../api/adminApi';

const CreateCardDialog = ({ open, onClose, onCardCreated }) => {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    user_id: ''
  });

  useEffect(() => {
    if (open) {
      setLoading(true);
      setFormData({ name: '', user_id: '' });
      setError('');
      setSubmitting(false);
      getUsers()
        .then(data => {
          setUsers(data);
          setLoading(false);
        })
        .catch(err => {
          console.error("Failed to fetch users", err);
          setError('Could not load user list.');
          setLoading(false);
        });
    }
  }, [open]);

  const handleClose = () => {
    setFormData({ name: '', user_id: '' });
    setError('');
    setLoading(true);
    setSubmitting(false);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_id || !formData.name) {
      setError('User and Card Name are required.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const selectedUser = users.find(u => u.id === parseInt(formData.user_id, 10));
      const dataToSend = {
        user_id: parseInt(formData.user_id, 10),
        name: formData.name,
        designation: '',
        company_name: '',
        email: selectedUser?.email || `user${formData.user_id}@example.com`,
        phone_number: '',
        address: ''
      };
      await createCardForUser(dataToSend);
      onCardCreated();
      handleClose();
    } catch (err) {
      console.error("Failed to create card", err);
      let errorMessage = 'An unexpected error occurred.';
      const errorDetail = err.response?.data?.detail;
      if (errorDetail) {
        if (Array.isArray(errorDetail)) {
          errorMessage = errorDetail.map(e => `Error on field '${e.loc[1]}': ${e.msg}`).join('; ');
        } else {
          errorMessage = String(errorDetail);
        }
      }
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedUser = users.find(u => u.id === parseInt(formData.user_id, 10));

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(145deg, rgba(18, 18, 18, 0.95) 0%, rgba(30, 30, 30, 0.9) 100%)'
            : 'linear-gradient(145deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: theme.palette.mode === 'dark' 
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 24px 64px rgba(0, 0, 0, 0.6)'
            : '0 24px 64px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
            {/* Header */}
            <DialogTitle sx={{ 
              p: 0, 
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              flexShrink: 0
            }}>
              <Box sx={{ 
                p: 3, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    border: '2px solid rgba(255,255,255,0.3)',
                    width: 48,
                    height: 48,
                    backdropFilter: 'blur(10px)'
                  }}>
                    <CardIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '1.2rem' }}>
                      Create Card
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Assign a digital card to a user
                    </Typography>
                  </Box>
                </Box>
                <IconButton 
                  onClick={handleClose}
                  sx={{ 
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': { 
                      bgcolor: 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <DialogContent sx={{ 
                p: 3, 
                flex: 1, 
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.3)' 
                    : 'rgba(0,0,0,0.2)',
                  borderRadius: '3px'
                }
              }}>
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3,
                      borderRadius: '12px'
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                    <CircularProgress size={48} sx={{ 
                      color: theme.palette.mode === 'dark' ? '#8b9cf7' : '#667eea' 
                    }} />
                    <Typography variant="h6" sx={{ ml: 3, color: 'text.primary' }}>
                      Loading users...
                    </Typography>
                  </Box>
                ) : (
                    <Stack spacing={3}>
                      <TextField
                        select
                        required
                        fullWidth
                        label="Select User"
                        name="user_id"
                        value={formData.user_id}
                        onChange={handleChange}
                        disabled={submitting}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px'
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Avatar sx={{ 
                                width: 32, 
                                height: 32, 
                                bgcolor: '#667eea',
                                mr: 1
                              }}>
                                <PersonIcon sx={{ fontSize: 16 }} />
                              </Avatar>
                            </InputAdornment>
                          )
                        }}
                      >
                        {users.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                              <Avatar sx={{ 
                                width: 40, 
                                height: 40,
                                bgcolor: user.is_active ? '#4CAF50' : '#FF5722',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>
                                {user.username.substring(0, 2).toUpperCase()}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="body1" fontWeight="600">
                                    {user.username}
                                  </Typography>
                                  {user.is_active && (
                                    <VerifiedIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  {user.email}
                                </Typography>
                              </Box>
                              <Chip 
                                icon={<CardIcon />}
                                label={`${user.cards?.length || 0} cards`}
                                size="small"
                                color={user.cards?.length > 0 ? "primary" : "default"}
                                variant="outlined"
                              />
                            </Stack>
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        required
                        fullWidth
                        label="Card Holder's Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        disabled={submitting}
                        placeholder="Enter the name to display on the card"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px'
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Avatar sx={{ 
                                width: 32, 
                                height: 32, 
                                bgcolor: '#f093fb',
                                mr: 1
                              }}>
                                <CardIcon sx={{ fontSize: 16 }} />
                              </Avatar>
                            </InputAdornment>
                          )
                        }}
                      />

                      {selectedUser && (
                        <Box sx={{ 
                          p: 3, 
                          borderRadius: '16px',
                          background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)'
                            : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                          border: theme.palette.mode === 'dark'
                            ? '1px solid rgba(102, 126, 234, 0.3)'
                            : '1px solid rgba(102, 126, 234, 0.2)'
                        }}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar sx={{ 
                              width: 48, 
                              height: 48,
                              bgcolor: selectedUser.is_active ? '#4CAF50' : '#FF5722',
                              fontWeight: 'bold'
                            }}>
                              {selectedUser.username.substring(0, 2).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" fontWeight="bold">
                                {selectedUser.username}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {selectedUser.email}
                              </Typography>
                            </Box>
                            <Stack spacing={1}>
                              <Chip 
                                label={selectedUser.is_active ? "Active" : "Inactive"}
                                color={selectedUser.is_active ? "success" : "error"}
                                size="small"
                              />
                              <Chip 
                                label={`${selectedUser.cards?.length || 0} cards`}
                                variant="outlined"
                                size="small"
                              />
                            </Stack>
                          </Stack>
                        </Box>
                      )}

                      <Divider sx={{ 
                        my: 2, 
                        borderColor: theme.palette.mode === 'dark'
                          ? 'rgba(102, 126, 234, 0.2)'
                          : 'rgba(102, 126, 234, 0.1)'
                      }} />

                      <Box sx={{ 
                        textAlign: 'center',
                        p: 2,
                        borderRadius: '12px',
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(102, 126, 234, 0.1)'
                          : 'rgba(102, 126, 234, 0.05)',
                        border: theme.palette.mode === 'dark'
                          ? '1px solid rgba(102, 126, 234, 0.2)'
                          : '1px solid rgba(102, 126, 234, 0.1)'
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          The card will be automatically assigned to the selected user and they can start customizing it immediately.
                        </Typography>
                      </Box>
                    </Stack>
                )}
              </DialogContent>

              <DialogActions sx={{ 
                p: 3, 
                pt: 0, 
                flexShrink: 0, 
                borderTop: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid rgba(0,0,0,0.05)'
              }}>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Button 
                    onClick={handleClose}
                    variant="outlined"
                    disabled={submitting}
                    sx={{ 
                      flex: 1,
                      py: 1.2,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '12px',
                      fontSize: '0.85rem'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="contained"
                    disabled={submitting || loading || !formData.user_id || !formData.name}
                    sx={{ 
                      flex: 1,
                      py: 1.2,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #43A047 0%, #1B5E20 100%)'
                      },
                      '&:disabled': {
                        background: 'linear-gradient(135deg, #E0E0E0 0%, #BDBDBD 100%)'
                      }
                    }}
                  >
                    {submitting ? (
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                    ) : (
                      'Create Card'
                    )}
                  </Button>
                </Stack>
              </DialogActions>
            </Box>
    </Dialog>
  );
};

export default CreateCardDialog; 